import { useId } from 'react'

export interface OpcaoDeFiltro {
  valor: string
  rotulo: string
}

interface Props {
  legenda: string
  /** Uma linha explicando o grupo, para quem não é de TI. Só onde o efeito não se deduz das caixas. */
  ajuda?: string
  opcoes: OpcaoDeFiltro[]
  selecionados: string[]
  aoAlternar: (valor: string, marcado: boolean) => void
}

/**
 * Grupo de caixas de seleção para um filtro de vários valores.
 *
 * Caixas, e não uma lista suspensa múltipla: `<select multiple>` exige Ctrl+clique para marcar mais
 * de um item, o que é invisível para quem não sabe, e some no celular. Aqui tudo o que está
 * disponível fica à vista, e o `<fieldset>` faz o leitor de tela anunciar "Prazo" antes de cada
 * opção.
 *
 * **Este componente é gêmeo do `features/gmud/lista/GrupoDeCaixas.tsx`, e a duplicação é
 * deliberada e temporária.** Ele não tem nada de chamado nem de solicitação: é um controle de
 * formulário, e o lugar dele é `components/`. Foi o segundo uso que provou isso — o primeiro não
 * prova nada. A promoção para `components/GrupoDeCaixas.tsx`, com os dois lados passando a importar
 * de lá, é uma edição só e cabe a quem cuida daquela pasta; enquanto ela não acontece, o que não
 * podia era um dos dois grupos de filtro nascer com marcação de acessibilidade diferente do outro.
 *
 * @param props legenda, opções, o que está marcado e o que fazer ao alternar
 * @returns o grupo de caixas
 */
export function GrupoDeCaixas({ legenda, ajuda, opcoes, selecionados, aoAlternar }: Props) {
  const base = useId()
  const idDaAjuda = `${base}-ajuda`

  return (
    // `aria-describedby` e não um texto solto: assim o leitor de tela anuncia a explicação junto do
    // grupo, e não só para quem enxerga a linha abaixo da legenda.
    <fieldset className="min-w-0" aria-describedby={ajuda ? idDaAjuda : undefined}>
      <legend className="text-sm font-medium text-slate-800">{legenda}</legend>
      {ajuda && (
        <p id={idDaAjuda} className="mt-1 max-w-prose text-xs text-slate-600">
          {ajuda}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
        {opcoes.map((opcao) => {
          const id = `${base}-${opcao.valor}`
          return (
            <div key={opcao.valor} className="flex items-center gap-2">
              <input
                id={id}
                type="checkbox"
                value={opcao.valor}
                checked={selecionados.includes(opcao.valor)}
                onChange={(evento) => aoAlternar(opcao.valor, evento.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-niteroi-700 focus:ring-niteroi-600"
              />
              <label htmlFor={id} className="text-sm text-slate-700">
                {opcao.rotulo}
              </label>
            </div>
          )
        })}
      </div>
    </fieldset>
  )
}
